import { Check, Sparkles, Zap, Star, ArrowRight, Shield, Clock, Users, BarChart3, Code, Settings } from 'lucide-react'

const plans = [
  {
    name: 'Free',
    price: 'Gratis',
    description: 'Perfecto para empezar a validar tus ideas',
    features: [
      {
        name: '1 proyecto activo',
        description: 'Valida una idea a la vez',
        icon: Star
      },
      {
        name: 'Hasta 100 visitas/mes',
        description: 'Ideal para pruebas iniciales',
        icon: Users
      },
      {
        name: 'Métricas básicas',
        description: 'Visitas, tiempo en página, clicks',
        icon: BarChart3
      },
      {
        name: 'Soporte por email',
        description: 'Respuesta en 24-48 horas',
        icon: Clock
      }
    ],
    cta: 'Comenzar gratis',
    popular: false,
    color: 'from-gray-500 to-gray-600'
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/mes',
    description: 'Para emprendedores y startups en crecimiento',
    features: [
      {
        name: '5 proyectos activos',
        description: 'Valida múltiples ideas simultáneamente',
        icon: Star
      },
      {
        name: 'Hasta 1,000 visitas/mes',
        description: 'Escala con tu crecimiento',
        icon: Users
      },
      {
        name: 'Métricas avanzadas',
        description: 'Análisis de comportamiento y funnels',
        icon: BarChart3
      },
      {
        name: 'Soporte prioritario',
        description: 'Respuesta en 4-8 horas',
        icon: Clock
      },
      {
        name: 'Exportación de datos',
        description: 'CSV, Excel, API',
        icon: Code
      },
      {
        name: 'Integraciones personalizadas',
        description: 'Conecta con tu stack',
        icon: Settings
      }
    ],
    cta: 'Probar Pro',
    popular: true,
    color: 'from-primary-500 to-primary-600'
  },
  {
    name: 'Ultimate',
    price: '$99',
    period: '/mes',
    description: 'Para equipos y empresas establecidas',
    features: [
      {
        name: 'Proyectos ilimitados',
        description: 'Sin restricciones de proyectos',
        icon: Star
      },
      {
        name: 'Visitas ilimitadas',
        description: 'Escala sin límites',
        icon: Users
      },
      {
        name: 'Métricas premium',
        description: 'Análisis avanzado y predicciones',
        icon: BarChart3
      },
      {
        name: 'Soporte 24/7',
        description: 'Asistencia inmediata',
        icon: Clock
      },
      {
        name: 'API completa',
        description: 'Acceso total a la API',
        icon: Code
      },
      {
        name: 'White label',
        description: 'Personalización total',
        icon: Settings
      },
      {
        name: 'SLA garantizado',
        description: '99.9% uptime',
        icon: Shield
      }
    ],
    cta: 'Contactar ventas',
    popular: false,
    color: 'from-purple-500 to-purple-600'
  }
]

/**
 * Pricing section component for the landing page
 * Displays different pricing plans with detailed features
 */
export function Pricing() {
  return (
    <section id="pricing" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Planes</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Elegí el plan que mejor se adapte a tus necesidades
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Comenzá gratis y escalá según crezca tu proyecto. Sin contratos largos ni costos ocultos.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-3">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-2xl border ${
                plan.popular ? 'border-primary-600 shadow-xl' : 'border-gray-200'
              } bg-white p-8 shadow-sm transition-all hover:shadow-lg`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-0 right-0 mx-auto w-32 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 px-3 py-1 text-center text-sm font-semibold text-white">
                  Más popular
                </div>
              )}

              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold leading-8 text-gray-900">{plan.name}</h3>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${plan.color}`}>
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                </div>

                <p className="mt-4 text-sm leading-6 text-gray-600">{plan.description}</p>

                <div className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-bold tracking-tight text-gray-900">{plan.price}</span>
                  {plan.period && (
                    <span className="text-sm font-semibold leading-6 text-gray-600">{plan.period}</span>
                  )}
                </div>

                <ul role="list" className="mt-8 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature.name} className="flex gap-x-3">
                      <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-50">
                        <feature.icon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={plan.name === 'Ultimate' ? '#contact' : '#'}
                className={`mt-8 block rounded-full px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  plan.popular
                    ? 'bg-primary-600 text-white hover:bg-primary-500 focus-visible:outline-primary-600'
                    : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                }`}
              >
                {plan.cta}
                <ArrowRight className="ml-2 inline-block h-4 w-4" />
              </a>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-gray-600">
            ¿Necesitás un plan personalizado?{' '}
            <a href="#contact" className="font-semibold text-primary-600 hover:text-primary-500">
              Contactanos
            </a>
          </p>
        </div>
      </div>
    </section>
  )
} 